<template>
  <div class="task-actions-detail">
    <TaskStatusDropdown
      :model-value="selectedStatus"
      :options="statuses"
      :is-disabled="statusDisabled"
      @update:model-value="emit('update:selectedStatus', $event)"
    />
    <TaskPriorityDropdown
      :model-value="selectedPriority"
      :options="priorities"
      :is-disabled="!editing"
      @update:model-value="emit('update:selectedPriority', $event)"
    />
    <TaskDifficultyDropdown
      :model-value="selectedDifficulty"
      :options="difficultyList"
      :is-disabled="!editing"
      @update:model-value="emit('update:selectedDifficulty', $event)"
    />
    <TaskTypeDropdown
      :model-value="selectedType"
      :options="typeList"
      :is-disabled="!editing"
      @update:model-value="emit('update:selectedType', $event)"
    />
    <TaskDeadline
      :model-value="deadlineDate"
      :editing="editing"
      @update:model-value="emit('update:deadlineDate', $event)"
    />
    <TaskTimeTracker
      ref="$timeTracker"
      :userAccess="userAccess"
      :records="timerRecords"
      :timer-start-date="timerStartDate"
      :is-loading="isLoadingTimer"
      :is-disabled="isTimeTrackerDisabled"
      width="100%"
      @on-start="emit('onStartTimer', $event)"
      @updateTimer="emit('updateTimer', $event)"
      @on-stop="emit('onStopTimer')"
      @on-create-record="emit('onCreateTimerRecord', $event)"
      @on-delete-record="emit('onDeleteTimerRecord', $event)"
      @on-edit-record="emit('onEditTimerRecord', $event)"
    />
    <TaskTimeEstimate
      ref="$timeEstimate"
      :userAccess="userAccess"
      :performers-estimated="performersEstimated"
      :is-disabled="!isEditMode"
      :is-loading="isLoadingEstimate"
      :taskId="taskId"
      :canUpsertFinalEstimate="canUpsertFinalEstimate"
      :finalEstimatePerformer="finalEstimatePerformer"
      :isEstimate="isEstimate"
    />
  </div>
</template>

<script lang="ts" setup>
import { computed, PropType, ref } from 'vue';
import {
  TaskStatusDropdown,
  TaskPriorityDropdown,
  TaskTypeDropdown,
  TaskDifficultyDropdown,
  TaskDeadline,
  TaskTimeTracker,
  TimeTrackerCreateRecordModel,
  TimerTrackerEditRecordModel,
  TimeTrackerRecordModel,
  TaskTimeEstimate,
} from '../../components';
import {
  DifficultyModel,
  PerformerEstimatedModel,
  PriorityModel,
  StatusModel,
  TypeModel,
} from '../../models';
import GetAccessCurrentUserInQueueTaskByIdResponseModel from './models/GetAccessCurrentUserInQueueTaskByIdResponseModel';

const emit = defineEmits<{
  /**Событие выбора статуса */
  (e: 'update:selectedStatus', value: number): void;
  /**Событие выбора приоритета */
  (e: 'update:selectedPriority', value: number): void;
  /**Событие выбора сложности */
  (e: 'update:selectedDifficulty', value: string): void;
  /**Событие выбора типа */
  (e: 'update:selectedType', value: string): void;
  /**Событие выбора дедлайна */
  (e: 'update:deadlineDate', value: Date): void;
  /**Событие старта таймера */
  (e: 'onStartTimer', isConfirm: boolean): void;
  /**Событие остановки таймера */
  (e: 'onStopTimer'): void;
  /**Событие создания новой записи таймера */
  (e: 'onCreateTimerRecord', value: TimeTrackerCreateRecordModel): void;
  /**Удать запись таймера */
  (e: 'onDeleteTimerRecord', timerId: number): void;
  /**Событие редактирования записи */
  (e: 'onEditTimerRecord', value: TimerTrackerEditRecordModel): void;
  (e: 'updateTimer', state: boolean);
  /**Событие оставноки таймера */
}>();

const props = defineProps({
  /**Доступы юзера к очереди */
  userAccess: { type: Object as PropType<GetAccessCurrentUserInQueueTaskByIdResponseModel> },
  /**Режим редактирования */
  isEditMode: {
    type: Boolean,
    default: false,
  },
  /**Отключить таймер */
  isDisabledTimer: {
    type: Boolean,
    default: false,
  },

  /**Выбранный статус */
  selectedStatus: {
    type: Number,
  },
  /**Выбранный приоритет */
  selectedPriority: {
    type: Number,
  },
  /**Выбранная сложность */
  selectedDifficulty: {
    type: String,
  },
  /**Выбранный тип */
  selectedType: {
    type: String,
  },
  /**Дата дедлайна */
  deadlineDate: {
    type: Date,
    default: new Date(),
  },
  /**Дата старта таймера */
  timerStartDate: {
    type: Date,
  },
  /**Загрузка таймера */
  isLoadingTimer: {
    type: Boolean,
    default: false,
  },
  /**Загрузка оценки */
  isLoadingEstimate: {
    type: Boolean,
    default: false,
  },
  /**разрешить редактирование */
  editing: {
    type: Boolean,
    default: true,
  },

  /**Список статусов */
  statuses: {
    type: Array as PropType<Array<StatusModel>>,
    default: [],
  },
  /**Список приоритетов */
  priorities: {
    type: Array as PropType<Array<PriorityModel>>,
    default: [],
  },
  /**Список уровней сложности */
  difficultyList: {
    type: Array as PropType<Array<DifficultyModel>>,
    default: [],
  },
  /**Список типов */
  typeList: {
    type: Array as PropType<Array<TypeModel>>,
    default: [],
  },
  /**Записи таймера */
  timerRecords: {
    type: Array as PropType<Array<TimeTrackerRecordModel>>,
    default: [],
  },
  /**Список оценок по исполнителям */
  performersEstimated: {
    type: Array as PropType<Array<PerformerEstimatedModel>>,
    default: [],
  },
  /**Id задачи */
  taskId: { type: Number },
  /**Может ли текущий пользователь выставлять итоговую оценку времени */
  canUpsertFinalEstimate: { type: Boolean },
  /**Финальная оценка */
  finalEstimatePerformer: { type: Object as PropType<PerformerEstimatedModel> },
  /**Оценка задачи */
  isEstimate: { type: Boolean },
  /**Разрешено редактирование статуса */
  allowStatusChange: { type: Boolean },
  /**Текущий пользователь является исполнителем */
  isPerformer: { type: Boolean },
});

/**Ссылка на компонент таймера */
const $timeTracker = ref<InstanceType<typeof TaskTimeTracker>>();
/**Ссылка на компонент оценки времени */
const $timeEstimate = ref<InstanceType<typeof TaskTimeEstimate>>();

/**Отключено ли редактирование статуса */
const statusDisabled = computed(() => !props.editing && !props.allowStatusChange);
/**Отключен учет времени */
const isTimeTrackerDisabled = computed(
  () => (!props.isEditMode || props.isDisabledTimer || !props.editing) && !props.isPerformer,
);

/**Код для использования по ref */
defineExpose({
  $timeTracker,
  $timeEstimate,
});
</script>

<style lang="scss" scoped>
.task-actions-detail {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}
</style>
